// src/pages/AdminDashboard.js - Fixed Pending Payments Calculation
import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase.config';
import UserDashboard from './UserDashboard';
import PaymentManagement from './PaymentManagement';
import QuickReminderModal from '../components/payments/QuickReminderModal';
import ExpenseManagement from '../components/expenses/ExpenseManagement';
import ServiceDirectory from '../services/ServiceDirectory';
import MaintenanceManagement from '../components/maintenance/MaintenanceManagement';
import MeetingManagement from '../components/meetings/MeetingManagement';
import './UserDashboard.css';

function AdminDashboard({ user, userData, onLogout }) {
  const [stats, setStats] = useState({
    totalCollected: 0,
    pendingPayments: 0,
    monthlyExpenses: 0,
    occupancy: 0,
    totalResidents: 0,
    paidResidents: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [activeView, setActiveView] = useState('overview');
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [activeTab, setActiveTab] = useState('user');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'admin' && activeView === 'overview') {
      fetchAdminData();
    }
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [activeTab, activeView, darkMode]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      
      // First, get all active residents (excluding superadmin)
      const { data: residents, error: residentsError } = await supabase
        .from('residents')
        .select('id, flat_number, name, role')
        .eq('account_status', 'active')
        .neq('role', 'superadmin');

      if (residentsError) {
        console.error('Error fetching residents:', residentsError);
      }

      const totalResidents = residents ? residents.length : 0;

      // Then get payments for current month
      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('amount, status, resident_id')
        .eq('payment_month', currentMonth)
        .eq('payment_year', currentYear);

      if (paymentsError) {
        console.error('Error fetching payments:', paymentsError);
      }

      // Calculate stats properly
      if (residents && payments) {
        // Get unique residents who have paid
        const paidResidentIds = new Set(
          payments
            .filter(p => p.status === 'paid')
            .map(p => p.resident_id)
        );
        
        const paidResidentsCount = paidResidentIds.size;
        
        // Pending = Total residents - Paid residents
        const pendingPaymentsCount = totalResidents - paidResidentsCount;
        
        // Total collected amount
        const totalCollected = payments
          .filter(p => p.status === 'paid')
          .reduce((sum, p) => sum + (p.amount || 0), 0);

        setStats(prev => ({
          ...prev,
          totalCollected,
          pendingPayments: pendingPaymentsCount,
          totalResidents,
          paidResidents: paidResidentsCount
        }));
      } else if (residents && !payments) {
        // If no payments exist for this month, all residents are pending
        setStats(prev => ({
          ...prev,
          totalCollected: 0,
          pendingPayments: totalResidents,
          totalResidents,
          paidResidents: 0
        }));
      }

      // Fetch expenses
      const { data: expenses } = await supabase
        .from('expenses')
        .select('amount')
        .eq('fiscal_month', currentMonth)
        .eq('fiscal_year', currentYear);

      if (expenses) {
        const monthlyExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
        setStats(prev => ({ ...prev, monthlyExpenses }));
      }

      // Calculate occupancy
      if (residents) {
        const totalFlats = 7; // Assuming 50 total flats, adjust as needed
        const occupiedFlats = [...new Set(residents.map(r => r.flat_number))].length;
        const occupancyRate = Math.round((occupiedFlats / totalFlats) * 100);
        
        setStats(prev => ({ 
          ...prev, 
          occupancyRate,
          occupiedFlats,
          totalFlats
        }));
      }

      // Fetch action items
      try {
        const { data: actionItems } = await supabase
          .from('action_items')
          .select('*')
          .eq('status', 'pending')
          .limit(5);
        
        if (actionItems) {
          setPendingActions(actionItems);
        }
      } catch (error) {
        console.log('Action items table might not exist:', error);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Handle admin sub-views
  if (activeTab === 'admin' && activeView === 'payments') {
    return <PaymentManagement 
      userData={userData} 
      userRole={userData.role} 
      onBack={() => setActiveView('overview')}
    />;
  }
  
  if (activeTab === 'admin' && activeView === 'expenses') {
    return <ExpenseManagement userData={userData} onBack={() => setActiveView('overview')} />;
  }
  
  if (activeTab === 'admin' && activeView === 'services') {
    return <ServiceDirectory userData={userData} onBack={() => setActiveView('overview')} />;
  }
  
  if (activeTab === 'admin' && activeView === 'maintenance') {
    return <MaintenanceManagement userData={userData} onBack={() => setActiveView('overview')} />;
  }
  
  if (activeTab === 'admin' && activeView === 'meetings') {
    return <MeetingManagement userData={userData} onBack={() => setActiveView('overview')} />;
  }

  // Main render
  if (activeTab === 'user') {
    return (
      <UserDashboard 
        user={user} 
        userData={userData} 
        onLogout={onLogout}
        isAdminUser={true}
        onSwitchToAdmin={() => {
          setActiveTab('admin');
          setActiveView('overview');
        }}
        activeTab={activeTab}
      />
    );
  }

  return (
    <div className={`dashboard-container admin-view ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* MOBILE HEADER FOR ADMIN PANEL */}
      <header className="mobile-header-admin">
        <button 
          className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <h2 className="mobile-title">Admin Panel</h2>
        <div className="mobile-spacer"></div>
      </header>

      <aside className={`dashboard-sidebar admin-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar admin-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Administrator</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          {/* Role Switcher Section */}
          <div className="role-switcher-section">
            <button
              className={`menu-item role-switch ${activeTab === 'user' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('user');
                setActiveView('overview');
                closeMobileMenu();
              }}
            >
              <span className="menu-icon">👤</span>
              <span className="menu-label">My Dashboard</span>
            </button>
            <button
              className={`menu-item role-switch ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('admin');
                setActiveView('overview');
                closeMobileMenu();
              }}
            >
              <span className="menu-icon">🛡️</span>
              <span className="menu-label">Admin Panel</span>
            </button>
          </div>
          
          <div className="sidebar-divider"></div>
          
          {/* Admin Menu Items */}
          <button
            className={`menu-item ${activeView === 'overview' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('overview');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📊</span>
            <span className="menu-label">Dashboard</span>
          </button>
          <button
            className={`menu-item ${activeView === 'payments' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('payments');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">💰</span>
            <span className="menu-label">Payments</span>
          </button>
          <button
            className={`menu-item ${activeView === 'expenses' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('expenses');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">💸</span>
            <span className="menu-label">Expenses</span>
          </button>
          <button
            className={`menu-item ${activeView === 'maintenance' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('maintenance');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">🔧</span>
            <span className="menu-label">Maintenance</span>
          </button>
          <button
            className={`menu-item ${activeView === 'meetings' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('meetings');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📅</span>
            <span className="menu-label">Meetings</span>
          </button>
          <button
            className={`menu-item ${activeView === 'services' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('services');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📞</span>
            <span className="menu-label">Services</span>
          </button>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          <button className="logout-button" onClick={onLogout}>
            <span className="menu-icon">🚪</span>
            <span className="menu-label">Logout</span>
          </button>
        </div>
      </aside>
      
      <main className="dashboard-main admin-main">
        <div className="dashboard-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading admin data...</p>
            </div>
          ) : (
            <div className="dashboard-grid">
              <div className="dashboard-card stat-card gradient-purple">
                <div className="stat-content">
                  <h3>💰 Total Collected</h3>
                  <p className="stat-value">₹{stats.totalCollected || 0}</p>
                  <small>This month</small>
                </div>
              </div>

              <div className="dashboard-card stat-card gradient-blue">
                <div className="stat-content">
                  <h3>👥 Pending Payments</h3>
                  <p className="stat-value">{stats.pendingPayments || 0}</p>
                  <small>{stats.paidResidents || 0}/{stats.totalResidents || 0} Paid</small>
                </div>
              </div>

              <div className="dashboard-card stat-card gradient-red">
                <div className="stat-content">
                  <h3>💸 Monthly Expenses</h3>
                  <p className="stat-value">₹{stats.monthlyExpenses || 0}</p>
                  <small>This month</small>
                </div>
              </div>

              <div className="dashboard-card stat-card gradient-yellow">
                <div className="stat-content">
                  <h3>🏠 Occupancy</h3>
                  <p className="stat-value">{stats.occupancyRate || 0}%</p>
                  <small>{stats.occupiedFlats || 0}/{stats.totalFlats || 0} flats</small>
                </div>
              </div>

              <div className="dashboard-card">
                <h3>⚠️ Pending Actions</h3>
                <div className="card-content">
                  {pendingActions.length > 0 ? (
                    pendingActions.map(action => (
                      <div key={action.id} className="action-item">
                        <strong>{action.description}</strong>
                        <small>Assigned: {action.assigned_to || 'All'}</small>
                        {action.due_date && (
                          <small>Due: {new Date(action.due_date).toLocaleDateString()}</small>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="empty-message">No pending actions</p>
                  )}
                </div>
              </div>

              <div className="dashboard-card">
                <h3>📊 Recent Activities</h3>
                <div className="card-content">
                  {recentActivities.length > 0 ? (
                    recentActivities.map(activity => (
                      <div key={activity.id} className="activity-item">
                        <strong>{activity.action}</strong>
                        <small>{new Date(activity.created_at).toLocaleString()}</small>
                      </div>
                    ))
                  ) : (
                    <p className="empty-message">No recent activities</p>
                  )}
                </div>
              </div>

              <div className="dashboard-card">
                <h3>⚡ Quick Actions</h3>
                <div className="quick-links-grid">
                  <button 
                    onClick={() => setActiveView('payments')}
                    className="quick-link-btn admin-quick-btn green"
                  >
                    💰 Payments
                  </button>
                  <button 
                    onClick={() => setShowReminderModal(true)}
                    className="quick-link-btn admin-quick-btn purple"
                  >
                    📧 Send Reminder
                  </button>
                  <button 
                    onClick={() => setActiveView('expenses')}
                    className="quick-link-btn admin-quick-btn yellow"
                  >
                    ➕ Add Expense
                  </button>
                  <button 
                    onClick={() => setActiveView('meetings')}
                    className="quick-link-btn admin-quick-btn red"
                  >
                    📅 Meetings
                  </button>
                  <button 
                    onClick={() => setActiveView('maintenance')}
                    className="quick-link-btn admin-quick-btn cyan"
                  >
                    🔧 Maintenance
                  </button>
                  <button 
                    onClick={() => setActiveView('services')}
                    className="quick-link-btn admin-quick-btn violet"
                  >
                    📞 Services
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <QuickReminderModal 
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        userData={userData}
      />
    </div>
  );
}

export default AdminDashboard;